// Demo story: the demo user, outlet staff, recycler managers, drivers, suppliers.

import { DEMO_OUTLET_NAME, DEMO_OUTLET_NAME_AR, DEMO_OUTLET_ORG_ID, DEMO_USER_ID, DEMO_USERNAME, ids } from '../constants';
import { daysFromNow, isoDate } from '../prng';
import { DEMO_COLLECTION_POINTS, DEMO_SITES, type DemoCity } from './places';

export interface DemoUser {
  id: string;
  username: string;
  email: string;
  full_name: string;
  phone: string;
}

export const DEMO_USER: DemoUser = {
  id: DEMO_USER_ID,
  username: DEMO_USERNAME,
  email: DEMO_USERNAME,
  full_name: 'Demo User',
  phone: '+966500000000',
};

// ── Outlet staff (12) ──────────────────────────────────────────────────────

export interface DemoStaffMember {
  id: string;
  full_name: string;
  full_name_ar: string;
  email: string;
  phone: string;
  role: 'staff' | 'ops_manager';
  job_title: string;
  job_title_ar: string;
  /** Sites this person works at (ops managers cover several). */
  site_ids: string[];
}

const S = DEMO_SITES.map((s) => s.id);

type StaffRow = [string, string, string, DemoStaffMember['role'], string, string, string[]];

const STAFF_ROWS: StaffRow[] = [
  ['Faisal Al-Qahtani', 'فيصل القحطاني', 'f.alqahtani', 'ops_manager', 'Head of Operations', 'رئيس العمليات', S],
  ['Noura Al-Harbi', 'نورة الحربي', 'n.alharbi', 'ops_manager', 'Sustainability Manager', 'مديرة الاستدامة', S],
  ['Omar Al-Zahrani', 'عمر الزهراني', 'o.alzahrani', 'ops_manager', 'Regional Operations Manager (West)', 'مدير العمليات الإقليمي (الغربية)', [S[4], S[5]]],
  ['Khalid Al-Otaibi', 'خالد العتيبي', 'k.alotaibi', 'staff', 'Branch Manager', 'مدير الفرع', [S[0]]],
  ['Sara Al-Dosari', 'سارة الدوسري', 's.aldosari', 'staff', 'Branch Manager', 'مديرة الفرع', [S[1]]],
  ['Abdullah Al-Shehri', 'عبدالله الشهري', 'a.alshehri', 'staff', 'Branch Manager', 'مدير الفرع', [S[2]]],
  ['Majed Al-Mutairi', 'ماجد المطيري', 'm.almutairi', 'staff', 'Warehouse Supervisor', 'مشرف المستودع', [S[3]]],
  ['Hind Al-Ghamdi', 'هند الغامدي', 'h.alghamdi', 'staff', 'Branch Manager', 'مديرة الفرع', [S[4]]],
  ['Yousef Al-Malki', 'يوسف المالكي', 'y.almalki', 'staff', 'Branch Manager', 'مدير الفرع', [S[5]]],
  ['Reem Al-Subaie', 'ريم السبيعي', 'r.alsubaie', 'staff', 'Waste Coordinator', 'منسقة النفايات', [S[0], S[1]]],
  ['Imran Siddiqui', 'عمران صديقي', 'i.siddiqui', 'staff', 'Store Associate', 'موظف المتجر', [S[2], S[3]]],
  ['Rahul Menon', 'راهول مينون', 'r.menon', 'staff', 'Logistics Clerk', 'كاتب لوجستي', [S[3]]],
];

export const DEMO_OUTLET_STAFF: DemoStaffMember[] = STAFF_ROWS.map(
  ([full_name, full_name_ar, handle, role, job_title, job_title_ar, site_ids], i) => ({
    id: ids.staff(i + 1),
    full_name,
    full_name_ar,
    email: `${handle}@alnakheel.example`,
    phone: `+9665000${String(10101 + i)}`,
    role,
    job_title,
    job_title_ar,
    site_ids: [...site_ids],
  }),
);

// ── Recycler managers (2) ──────────────────────────────────────────────────

export interface DemoManager {
  id: string;
  full_name: string;
  full_name_ar: string;
  email: string;
  phone: string;
  role: 'ops_manager';
  job_title: string;
  facility_id: string;
}

export const DEMO_RECYCLER_MANAGERS: DemoManager[] = [
  {
    id: ids.manager(1),
    full_name: 'Tariq Al-Rashid',
    full_name_ar: 'طارق الراشد',
    email: 't.alrashid@riyadhgreen.example',
    phone: '+966500020101',
    role: 'ops_manager',
    job_title: 'Operations Manager',
    facility_id: ids.facility(1),
  },
  {
    id: ids.manager(2),
    full_name: 'Mona Al-Fahad',
    full_name_ar: 'منى الفهد',
    email: 'm.alfahad@riyadhgreen.example',
    phone: '+966500020102',
    role: 'ops_manager',
    job_title: 'Logistics Manager',
    facility_id: ids.facility(2),
  },
];

// ── Drivers (10) ───────────────────────────────────────────────────────────

export interface DemoDriver {
  id: string;
  full_name: string;
  full_name_ar: string;
  email: string;
  phone: string;
  licence_no: string;
  /** 'YYYY-MM-DD' */
  licence_expiry: string;
  city: DemoCity;
  home_facility_id: string;
  status: 'active' | 'off_duty';
}

// [name, name_ar, handle, saudi national?, licence expiry in days, city, status]
type DriverRow = [string, string, string, boolean, number, DemoCity, DemoDriver['status']];

const DRIVER_ROWS: DriverRow[] = [
  ['Mohammed Al-Shammari', 'محمد الشمري', 'm.alshammari', true, 640, 'Riyadh', 'active'],
  ['Saad Al-Anazi', 'سعد العنزي', 's.alanazi', true, 410, 'Riyadh', 'active'],
  ['Ahmed Al-Juhani', 'أحمد الجهني', 'a.aljuhani', true, 905, 'Riyadh', 'active'],
  ['Fahad Al-Yami', 'فهد اليامي', 'f.alyami', true, 22, 'Riyadh', 'active'],
  ['Bandar Al-Rasheedi', 'بندر الرشيدي', 'b.alrasheedi', true, 330, 'Riyadh', 'active'],
  ['Muhammad Asif', 'محمد آصف', 'm.asif', false, 515, 'Riyadh', 'active'],
  ['Rashid Khan', 'راشد خان', 'r.khan', false, 270, 'Jeddah', 'active'],
  ['Sajid Hussain', 'ساجد حسين', 's.hussain', false, 780, 'Jeddah', 'active'],
  ['Anil Kumar', 'أنيل كومار', 'a.kumar', false, 150, 'Riyadh', 'off_duty'],
  ['Shafiq Rahman', 'شفيق رحمن', 's.rahman', false, 455, 'Jeddah', 'active'],
];

export const DEMO_DRIVERS: DemoDriver[] = DRIVER_ROWS.map(
  ([full_name, full_name_ar, handle, saudi, expiryDays, city, status], i) => ({
    id: ids.driver(i + 1),
    full_name,
    full_name_ar,
    email: `${handle}@riyadhgreen.example`,
    phone: `+9665000${String(30101 + i)}`,
    // Saudi licences carry the national id (starts 1), residents the iqama number (starts 2).
    licence_no: `${saudi ? '10' : '24'}${String(48213570 + i * 7919).padStart(8, '0')}`,
    licence_expiry: isoDate(daysFromNow(expiryDays)),
    city,
    home_facility_id: city === 'Jeddah' ? ids.facility(2) : ids.facility(1),
    status,
  }),
);

export function driverById(id: string): DemoDriver | undefined {
  return DEMO_DRIVERS.find((d) => d.id === id);
}

// ── Suppliers (5) ──────────────────────────────────────────────────────────

export interface DemoSupplier {
  id: string;
  name_en: string;
  name_ar: string;
  /** Set for a supplier that is itself a Recyclee organization (the demo outlet). */
  linked_organization_id: string | null;
  supplier_type: 'retail' | 'hospitality' | 'healthcare' | 'education';
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  city: DemoCity;
  lat: number;
  lng: number;
  address: string;
  status: 'active';
  collection_point_ids: string[];
}

function pointsOf(supplierId: string): string[] {
  return DEMO_COLLECTION_POINTS.filter((p) => p.supplier_id === supplierId).map((p) => p.id);
}

export const DEMO_SUPPLIERS: DemoSupplier[] = [
  {
    id: ids.supplier(1),
    name_en: DEMO_OUTLET_NAME,
    name_ar: DEMO_OUTLET_NAME_AR,
    linked_organization_id: DEMO_OUTLET_ORG_ID,
    supplier_type: 'retail',
    contact_name: 'Noura Al-Harbi',
    contact_phone: '+966500010102',
    contact_email: 'n.alharbi@alnakheel.example',
    city: 'Riyadh',
    lat: 24.7005,
    lng: 46.68,
    address: 'Olaya St, Al Olaya, Riyadh 12214',
    status: 'active',
    collection_point_ids: pointsOf(ids.supplier(1)),
  },
  {
    id: ids.supplier(2),
    name_en: 'Al Faisaliah Hotel',
    name_ar: 'فندق الفيصلية',
    linked_organization_id: null,
    supplier_type: 'hospitality',
    contact_name: 'Hassan Al-Amri',
    contact_phone: '+966500040101',
    contact_email: 'engineering@faisaliah-hotel.example',
    city: 'Riyadh',
    lat: 24.6903,
    lng: 46.6851,
    address: 'King Fahd Rd, Al Olaya, Riyadh 12212',
    status: 'active',
    collection_point_ids: pointsOf(ids.supplier(2)),
  },
  {
    id: ids.supplier(3),
    name_en: 'King Fahd Medical City campus',
    name_ar: 'حرم مدينة الملك فهد الطبية',
    linked_organization_id: null,
    supplier_type: 'healthcare',
    contact_name: 'Dr. Lama Al-Saud',
    contact_phone: '+966500040102',
    contact_email: 'facilities@kfmc.example',
    city: 'Riyadh',
    lat: 24.6883,
    lng: 46.7064,
    address: 'Al Sulimaniyah, Riyadh 12231',
    status: 'active',
    collection_point_ids: pointsOf(ids.supplier(3)),
  },
  {
    id: ids.supplier(4),
    name_en: 'Granada Mall',
    name_ar: 'غرناطة مول',
    linked_organization_id: null,
    supplier_type: 'retail',
    contact_name: 'Waleed Al-Harthi',
    contact_phone: '+966500040103',
    contact_email: 'operations@granadamall.example',
    city: 'Riyadh',
    lat: 24.7795,
    lng: 46.7307,
    address: 'Eastern Ring Rd, Al Shuhada, Riyadh 13241',
    status: 'active',
    collection_point_ids: pointsOf(ids.supplier(4)),
  },
  {
    id: ids.supplier(5),
    name_en: 'Riyadh Schools District',
    name_ar: 'إدارة مدارس الرياض',
    linked_organization_id: null,
    supplier_type: 'education',
    contact_name: 'Abeer Al-Shahrani',
    contact_phone: '+966500040104',
    contact_email: 'services@riyadhschools.example',
    city: 'Riyadh',
    lat: 24.8128,
    lng: 46.6351,
    address: 'Al Yasmin, Riyadh 13325',
    status: 'active',
    collection_point_ids: pointsOf(ids.supplier(5)),
  },
];

export function supplierById(id: string): DemoSupplier | undefined {
  return DEMO_SUPPLIERS.find((s) => s.id === id);
}
